import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLenis } from "lenis/react";
import { ArrowLeft, ArrowUpRight, Linkedin, Mail } from "lucide-react";
import { ARTICLES } from "@/data/articles";
import PlaybookCard from "@/components/PlaybookCard";

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const lenis = useLenis();
  const article = ARTICLES.find((a) => a.slug === slug);

  useEffect(() => {
    lenis?.scrollTo(0, { immediate: true });
    if (!article) return;
    document.title = `${article.title} | Makkar Growth Partners`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", article.excerpt);
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "article-jsonld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      author: { "@type": "Person", name: "Aspen Makkar" },
      publisher: { "@type": "Organization", name: "Makkar Growth Partners" },
      datePublished: article.date,
    });
    document.head.appendChild(ld);
    return () => document.getElementById("article-jsonld")?.remove();
  }, [article, lenis]);

  const goHome = (target) => {
    navigate("/");
    setTimeout(() => lenis?.scrollTo(target, { offset: -72, duration: 1.4 }), 350);
  };

  if (!article) {
    return (
      <main className="bg-paper text-ink min-h-screen flex flex-col items-center justify-center px-6">
        <p className="overline-label">The Insights Journal</p>
        <h1 className="mt-6 font-serif text-4xl font-light">Article not found.</h1>
        <Link data-testid="article-not-found-home" to="/" className="mt-8 text-xs uppercase tracking-[0.2em] border-b border-ink/20 pb-1 hover:border-taupe transition-colors duration-300">
          Back to the journal
        </Link>
      </main>
    );
  }

  const others = ARTICLES.filter((a) => a.slug !== slug);
  const articleUrl = `${window.location.origin}/insights/${article.slug}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}`;
  const mailtoUrl = `mailto:?subject=${encodeURIComponent(`${article.title} — Makkar Growth Partners`)}&body=${encodeURIComponent(`${article.excerpt}\n\n${articleUrl}`)}`;

  return (
    <main data-testid="article-page" className="bg-paper text-ink min-h-screen">
      <header className="border-b border-line bg-paper/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto max-w-[100rem] flex items-center justify-between px-6 md:px-12 py-4">
          <Link to="/" data-testid="article-nav-logo" className="leading-none">
            <span className="font-serif text-xl md:text-2xl font-medium tracking-tight">Makkar</span>
            <span className="block text-[10px] uppercase tracking-[0.3em] text-ash mt-1">Growth Partners</span>
          </Link>
          <button
            data-testid="article-back-btn"
            onClick={() => goHome("#insights")}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ash hover:text-ink transition-colors duration-300"
          >
            <ArrowLeft size={14} /> The Journal
          </button>
        </div>
      </header>

      <article className="px-6 md:px-12 py-16 md:py-28">
        <div className="mx-auto max-w-3xl">
          <p className="overline-label" data-testid="article-category">
            The Insights Journal · {article.number} — {article.category}
          </p>
          <h1 className="mt-6 font-serif text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">
            {article.title}
          </h1>
          <p className="mt-10 font-serif italic text-2xl md:text-3xl font-light leading-relaxed text-ink/80 border-l-2 border-taupe pl-6">
            {article.excerpt}
          </p>
          <div className="mt-12 space-y-8">
            {article.body.map((b, i) =>
              typeof b === "string" ? (
                <p key={i} className="text-base md:text-lg font-light text-ash leading-relaxed">
                  {b}
                </p>
              ) : b.h ? (
                <h2 key={i} className="pt-6 font-serif text-2xl md:text-3xl font-light tracking-tight text-ink">
                  {b.h}
                </h2>
              ) : (
                <p key={i} className="text-base md:text-lg font-light text-ash leading-relaxed">
                  <strong className="font-medium text-ink">{b.lead}</strong> {b.text}
                </p>
              )
            )}
          </div>
          <p className="mt-14 font-serif text-3xl md:text-4xl font-light tracking-tight leading-snug">
            <em className="text-taupe">{article.closing}</em>
          </p>

          <div className="mt-10 flex items-center gap-4">
            <span className="text-[10px] uppercase tracking-[0.25em] text-ash">Share this thinking</span>
            <a
              data-testid="article-share-linkedin"
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share on LinkedIn"
              className="flex h-11 w-11 items-center justify-center border border-line hover:bg-ink hover:text-paper hover:border-ink transition-colors duration-300"
            >
              <Linkedin size={16} />
            </a>
            <a
              data-testid="article-share-email"
              href={mailtoUrl}
              aria-label="Share by email"
              className="flex h-11 w-11 items-center justify-center border border-line hover:bg-ink hover:text-paper hover:border-ink transition-colors duration-300"
            >
              <Mail size={16} />
            </a>
          </div>

          <PlaybookCard />

          <div className="mt-16 border-t border-line pt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <p className="font-serif italic text-xl text-ink/80">Put this thinking to work.</p>
            <button
              data-testid="article-book-call-btn"
              onClick={() => goHome("#book")}
              className="group inline-flex items-center gap-2 bg-ink text-paper text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-taupe transition-colors duration-300"
            >
              Book a Free Strategy Call
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>

          <div className="mt-16">
            <p className="overline-label mb-6">More from the journal</p>
            {others.map((a) => (
              <Link
                key={a.slug}
                to={`/insights/${a.slug}`}
                data-testid={`article-related-${a.slug}`}
                className="group flex items-center justify-between gap-6 border-t border-line py-6"
              >
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-ash">{a.category}</p>
                  <p className="mt-1 font-serif text-xl md:text-2xl font-light tracking-tight group-hover:text-taupe transition-colors duration-300">
                    {a.title}
                  </p>
                </div>
                <ArrowUpRight size={18} className="shrink-0 text-taupe transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            ))}
            <div className="border-t border-line" />
          </div>
        </div>
      </article>
    </main>
  );
};

export default ArticlePage;
